import { CheckBox, NVButton, Select, TextField } from '@nv/basic-components'
import { AlertBanner } from '@nv/components'
import { useConfigurationContext, useRegistrationUserContext } from '@nv/contexts'
import HandIcon from '@nv/images/design/hand-icon'
import { days, months, years } from '@nv/utils'
import { useFormik } from 'formik'
import VoterRegistrationService from 'libs/services/src/lib/voter/VoterRegistrationService'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import * as Yup from 'yup'
import { AddressInput } from '../address/AddressInput'
import styles from './RegistrationStep1.module.scss'

export const RegistrationStep1 = ({ declarations = [] }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { registrationUser, setRegistrationUser } = useRegistrationUserContext()
  const { votingMethodOptions } = useConfigurationContext()
  const { electionDate, minAge, includeSingleNameField, defaultVotingMethod } = useConfigurationContext()

  const validationSchema = Yup.object()
    .shape({
      votingMethod: Yup.string().required(t('form.validations.votingMethodRequired')),
      addressNotListed: Yup.boolean(),

      searchAddress: Yup.object().when('addressNotListed', {
        is: false,
        then: () =>
          Yup.object().shape({
            streetAddress: Yup.string().required(t('form.validations.address')),
          }),
        otherwise: () => Yup.object().notRequired(),
      }),
      address: Yup.object().when('addressNotListed', {
        is: true,
        then: () =>
          Yup.object().shape({
            streetAddress: Yup.string().required(t('form.validations.street')),
            unitNumberPOBox: Yup.string().notRequired(),
            city: Yup.string().required(t('form.validations.city')),
            province: Yup.string().required(t('form.validations.province')),
            postalCode: Yup.string().required(t('form.validations.postalCode')),
          }),
        otherwise: () => Yup.object().notRequired(),
      }),
      firstName: Yup.string().required(t('admin.users.firstNameRequired')),
      lastName: Yup.string().when('singleName', {
        is: false, // Only required when singleName is false
        then: Yup.string().required(t('admin.users.lastNameRequired')),
        otherwise: Yup.string(), // Optional when singleName is true
      }),
      dobMonth: Yup.string().required(t('admin.users.monthRequired')),
      dobDay: Yup.string().required(t('admin.users.dayRequired')),
      dobYear: Yup.string().required(t('admin.users.yearRequired')),
      ...declarations.reduce((acc, curr, i) => {
        if (curr.isRequired)
          acc[curr.field] = Yup.boolean()
            .oneOf([true], t('registration.form.validations.mustAgreeCommunications'))
            .required(t('registration.form.validations.mustAgreeCommunications'))
        else acc[curr.field] = Yup.boolean()
        return acc
      }, {}),
      singleName: Yup.boolean(),
    })
    .test('is-valid-date', function (value) {
      const { dobDay, dobMonth, dobYear } = value
      if (!dobDay || !dobMonth || !dobYear) return false
      const day = parseInt(dobDay)
      const month = parseInt(dobMonth)
      const year = parseInt(dobYear)
      const date = new Date(year, month - 1, day)
      const isValidDate = date.getDate() === day && date.getMonth() === month - 1 && date.getFullYear() === year
      return isValidDate || this.createError({ path: 'dobYear', message: t('registration.form.validations.validBirthDate') })
    })
    .test('is-over-18', function (value) {
      const { dobDay, dobMonth, dobYear } = value
      if (!dobDay || !dobMonth || !dobYear) return false
      const day = parseInt(dobDay)
      const month = parseInt(dobMonth)
      const year = parseInt(dobYear)
      const dateCompareBirthday = electionDate ? electionDate : new Date()
      const birthDate = new Date(year, month - 1, day)
      const age = dateCompareBirthday.getFullYear() - birthDate.getFullYear()
      const monthDiff = dateCompareBirthday.getMonth() - birthDate.getMonth()
      const dayDiff = dateCompareBirthday.getDate() - birthDate.getDate()
      const isOverMinAge = age > minAge || (age === minAge && (monthDiff > 0 || (monthDiff === 0 && dayDiff >= 0)))
      return isOverMinAge || this.createError({ path: 'dobYear', message: t('registration.form.validations.minAge', { age: minAge }) })
    })

  const parsedData = registrationUser

  const formik = useFormik({
    initialValues: {
      ...registrationUser,
      votingMethod: parsedData?.votingMethod || defaultVotingMethod || '',
      firstName: parsedData?.firstName || '',
      middleName: parsedData?.middleName || '',
      lastName: parsedData?.lastName || '',
      singleName: parsedData?.singleName || false,
      dobMonth: parsedData?.dobMonth || '',
      dobDay: parsedData?.dobDay || '',
      dobYear: parsedData?.dobYear || '',
      isVoter: parsedData?.isVoter || false,
      ...declarations.reduce((acc, curr) => {
        acc[curr.field] = parsedData?.[curr.field] || false
        return acc
      }, {}),
      addressNotListed: registrationUser?.addressNotListed || false,
      searchAddress: {
        streetAddress: registrationUser?.searchAddress?.streetAddress || '',
        unitNumberPOBox: registrationUser?.searchAddress?.unitNumberPOBox || '',
        city: registrationUser?.searchAddress?.city || '',
        province: registrationUser?.searchAddress?.province || '',
        postalCode: registrationUser?.searchAddress?.postalCode || '',
        formattedAddress: registrationUser?.searchAddress?.formattedAddress || '',
        _id: registrationUser?.searchAddress?._id || '',
        value: registrationUser?.searchAddress?.value || '',
      },
      address: {
        streetAddress: registrationUser?.address?.streetAddress || '',
        unitNumberPOBox: registrationUser?.address?.unitNumber || '',
        city: registrationUser?.address?.city || '',
        province: registrationUser?.address?.province || '',
        postalCode: registrationUser?.address?.postalCode || '',
      },
    },
    validationSchema,
    validateOnChange: true,
    onSubmit: async (values) => {
      const updatedData = {
        ...registrationUser,
        ...values,
        dateOfBirth: `${values.dobYear}-${values.dobMonth.toString().padStart(2, '0')}-${values.dobDay.toString().padStart(2, '0')}`,

        votingChannel: values.votingMethod,
      }
      setRegistrationUser(updatedData)
      const lookupOnList = await VoterRegistrationService.voterLookup({
        ...updatedData,
        address: updatedData.addressNotListed ? updatedData.address : updatedData.searchAddress,
      })
      if (lookupOnList.onElectorsList) {
        setRegistrationUser({ ...updatedData, onElectorsList: true })
      }
      navigate('/registration/step2')
    },
  })
  return (
    <div className={styles['container']}>
      <div className={styles['title']}>{t('registration.voterRegistration')}</div>
      <form onSubmit={formik.handleSubmit} className={styles['form']}>
        <div className={styles['form__subtitle']}>{t('registration.registerForParticipation')}</div>

        {/* Voting Method Section */}
        {votingMethodOptions?.length > 1 && (
          <>
            <div className={styles['form__voting-method']}>
              <Select
                label={t('registration.selectVotingChannel')}
                formik={formik}
                options={votingMethodOptions}
                field='votingMethod'
                value={formik.values.votingMethod}
                isRequired={true}
              />
            </div>
            <AlertBanner
              icon={<HandIcon color='var(--error-color)' style={{ width: 50, height: 50 }} />}
              text={t('registration.voterMethodPrompt')}
            />
          </>
        )}
        {/* Name Section */}
        <div className={styles['form__header']}>{t('registration.personalInformamation')}</div>
        <div className={styles['form__name']}>
          <TextField
            formik={formik}
            type='text'
            field='firstName'
            placeholder={t('form.labels.firstName')}
            label={t('form.labels.firstName')}
            isRequired={true}
          />
          <TextField
            formik={formik}
            type='text'
            field='middleName'
            placeholder={t('form.labels.middleName')}
            label={t('form.labels.middleName')}
            isRequired={false}
          />
          <TextField
            formik={formik}
            type='text'
            field='lastName'
            placeholder={t('form.labels.lastName')}
            label={t('form.labels.lastName')}
            isRequired={!formik.values.singleName}
          />
        </div>
        {includeSingleNameField && (
          <CheckBox
            formik={formik}
            field='singleName'
            checked={formik.values.singleName}
            onChange={() => formik.setFieldValue('singleName', !formik.values.singleName)}
            label={t('registration.singleName')}
            square={true}
          />
        )}
        <AlertBanner
          icon={<HandIcon color='var(--error-color)' style={{ width: 50, height: 50 }} />}
          text={t('registration.fullLegalNamePrompt')}
        />
        {/* Date of Birth */}
        <div className={styles['form__birth']}>
          <div className={styles['form__select-container']}>
            <div className={styles['form__item-title']}>
              {t('form.labels.dateOfBirth')} <span className={styles['form__required']}>*</span>
            </div>
            <div className={styles['form__selects']}>
              <Select
                label={t('countdown.time.calendar.day')}
                formik={formik}
                options={days}
                field='dobDay'
                value={formik.values.dobDay}
                isRequired={true}
                ariaLabel={t('registration.dateOfBirthDay')}
                isSearchable={true}
              />
              <Select
                label={t('countdown.time.calendar.month')}
                formik={formik}
                options={months()}
                field='dobMonth'
                value={formik.values.dobMonth}
                isRequired={true}
                ariaLabel={t('registration.dateOfBirthMonth')}
                isSearchable={true}
              />
              <Select
                label={t('countdown.time.calendar.year')}
                formik={formik}
                options={years}
                field='dobYear'
                value={formik.values.dobYear}
                isRequired={true}
                ariaLabel={t('registration.dateOfBirthYear')}
                isSearchable={true}
              />
            </div>
          </div>
        </div>

        <div className={styles['form__header']}>{t('registration.qualifyingResidentialAddress')}</div>
        <AddressInput formik={formik} styles={styles} />

        <div className={styles['hr']}></div>
        {declarations.map((declaration, i) => {
          return (
            <CheckBox
              key={i}
              formik={formik}
              field={declaration.field}
              checked={formik.values[declaration.field]}
              onChange={() => formik.setFieldValue(declaration.field, !formik.values[declaration.field])}
              label={declaration.label}
              ariaLabel={declaration.label}
              square={true}
              isRequired={declaration.isRequired}
            />
          )
        })}

        {/* Buttons */}
        <div className={styles['form__actions']}>
          <NVButton
            variant='primary'
            text={t('buttons.cancel')}
            height={50}
            onClick={() => {
              setRegistrationUser(null)
              navigate('/')
            }}
          />
          <NVButton text={t('buttons.next')} height={50} onClick={formik.handleSubmit} />
        </div>
        <div className={styles['privacyStatement']}>{t('registration.privacyStatement')}</div>
      </form>
    </div>
  )
}
